/**
 * Tiny pure bounded-concurrency runner, mirroring
 * client/src/utils/request-generation.js's own "small, React-independent,
 * directly testable" convention -- no hooks, no JSX.
 *
 * Runs `worker` over `items` with at most `limit` calls in flight at once.
 * One item's rejection never aborts the others: each item's outcome is
 * captured independently as { ok: true, value } or { ok: false, error },
 * and the internal Promise.all only ever awaits the limit-bounded worker
 * LOOPS themselves (which never reject), never an individual `worker` call
 * directly.
 */

/**
 * @template T
 * @param {Array<T>} items
 * @param {number} limit - positive integer; effectively items.length if larger
 * @param {(item: T, index: number) => Promise<any>} worker
 * @param {(item: T, index: number, outcome: {ok: boolean, value?: any, error?: any}) => void} [onSettled]
 *   Fires synchronously the instant that item's own outcome is known --
 *   before every other item has necessarily settled -- so a caller can
 *   render live per-item progress instead of waiting for the whole batch.
 * @returns {Promise<Array<{ok: boolean, value?: any, error?: any}>>}
 *   Outcomes in the SAME order as `items` (not completion order).
 */
export async function runWithLimitedConcurrency(
  items,
  limit,
  worker,
  onSettled
) {
  const outcomes = new Array(items.length);
  let nextIndex = 0;

  async function runNext() {
    const currentIndex = nextIndex;
    nextIndex += 1;
    if (currentIndex >= items.length) {
      return;
    }
    const item = items[currentIndex];
    let outcome;
    try {
      const value = await worker(item, currentIndex);
      outcome = { ok: true, value };
    } catch (error) {
      outcome = { ok: false, error };
    }
    outcomes[currentIndex] = outcome;
    if (onSettled) {
      onSettled(item, currentIndex, outcome);
    }
    return runNext();
  }

  const workerCount = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: workerCount }, runNext));
  return outcomes;
}
